import React from 'react';
import Appear from './ui/Appear';

/**
 * A show's transport, as the two independent facts it actually is.
 *
 * "Driving" is DERIVED from an assigned vehicle, never read off a column. The
 * physical order is what forces this: our own truck does the drive out, and a
 * rental is picked up at the far end. You do not rent a van to drive out and find
 * your own truck waiting there. So an assigned truck settles "are we driving",
 * and `transport_mode` only ever describes the leg somebody else carries.
 *
 * That leaves `transport_mode` overloaded for one legacy reason: on a show with
 * NO external transport it is still the answer to "how does the gear leave HQ",
 * which is what the tracking-number fields, `event-locks.js` and the Packing page
 * read. Hence the fallback branch below. If that column is ever split, this
 * component is the only place the display rule lives.
 *
 * Cases, in the order they are decided:
 *   truck + arranged  -> two chips: Driving, plus Renting or Shipping
 *   truck only        -> Driving
 *   arranged only     -> Renting or Shipping (a rented truck, or freight)
 *   neither           -> the planned mode, which is the unsorted show's default
 */
export interface TransportChipsProps {
  /** Whether an `event_vehicle` row exists. THE source for "driving". */
  hasVehicle: boolean;
  /** `events.transport_arranged` — external transport is deliberately planned. */
  transportArranged: boolean;
  /** `events.transport_mode` — the flavor of the external leg. */
  transportMode: string | null | undefined;
  /** `compact` matches the dense pill rows on the hover card. */
  size?: 'default' | 'compact';
  className?: string;
}

const SIZED = {
  default: 'inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ring-1 ring-inset',
  compact: 'inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ring-1 ring-inset',
};

const TONE = {
  driving: 'bg-sky-500/15 day:bg-sky-100 text-sky-300 day:text-sky-800 ring-sky-500/30 day:ring-sky-300',
  renting: 'bg-amber-500/15 day:bg-amber-100 text-amber-300 day:text-amber-800 ring-amber-500/30 day:ring-amber-300',
  shipping: 'bg-purple-500/15 day:bg-purple-100 text-purple-300 day:text-purple-800 ring-purple-500/30 day:ring-purple-300',
};

/** Truck glyph, on the owned-truck chip only: there it carries meaning rather
 *  than decorating, and a second icon beside it would just be noise. */
const TruckIcon: React.FC = () => (
  <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
  </svg>
);

/** True when the gear leaves on wheels rather than with a carrier: our truck, or
 *  a rented one. Drives the "Leaves / Returns" vs "Ships Out / Ships Back" label
 *  pair, so it lives beside the chips that make the same distinction visible. */
export function drivesOut(hasVehicle: boolean, transportMode: string | null | undefined): boolean {
  return hasVehicle || transportMode === 'driving';
}

const TransportChips: React.FC<TransportChipsProps> = ({
  hasVehicle, transportArranged, transportMode, size = 'default', className = '',
}) => {
  const BASE = SIZED[size];
  const icon = size === 'compact' ? null : <TruckIcon />;
  const external = transportArranged
    ? (transportMode === 'driving'
        ? { label: 'Renting', tone: TONE.renting, title: 'A rented vehicle is carrying the gear' }
        : { label: 'Shipping', tone: TONE.shipping, title: 'The gear ships as freight' })
    : null;

  // Nothing assigned and nothing arranged: the show still carries a planned mode,
  // and that is what every surface showed before there was anything to add to it.
  const fallback = !hasVehicle && !external
    ? (transportMode === 'driving'
        ? { label: 'Driving', tone: TONE.driving, title: 'Planned to drive' }
        : { label: 'Shipping', tone: TONE.shipping, title: 'Planned to ship' })
    : null;

  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
      <Appear show={hasVehicle} className={`${BASE} ${TONE.driving}`}>
        <span title="A Firefly vehicle is assigned to this show" className="inline-flex items-center gap-[inherit]">
          {icon}
          Driving
        </span>
      </Appear>
      <Appear show={!!external} className={`${BASE} ${external?.tone ?? ''}`}>
        <span title={external?.title}>{external?.label}</span>
      </Appear>
      <Appear show={!!fallback} className={`${BASE} ${fallback?.tone ?? ''}`}>
        <span title={fallback?.title} className="inline-flex items-center gap-[inherit]">
          {fallback?.label === 'Driving' && icon}
          {fallback?.label}
        </span>
      </Appear>
    </span>
  );
};

export default TransportChips;
